import { explicitWindow, lastDaysWindow, type Bucket, type Window } from '../lib/time.ts'
import { h } from '../charts/svg.ts'

export type RangeMode =
  | { kind: 'preset'; days: number }
  | { kind: 'custom'; from: string; to: string }

export interface FilterState {
  range: RangeMode
  bucket: Bucket
  /**
   * Ask the cloud to aggregate into the bucket (`period`) where the attribute
   * offers it. Off, every raw row is fetched and summed here — slower, but the
   * unambiguous baseline to validate the server's buckets against.
   */
  serverPeriod: boolean
}

const PRESETS = [
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
]

const BUCKETS: Bucket[] = ['hour', 'day', 'week', 'month']

export function windowFor(range: RangeMode): Window {
  return range.kind === 'preset' ? lastDaysWindow(range.days) : explicitWindow(range.from, range.to)
}

/** A bucket finer than the window can afford makes an unreadable axis. */
export function defaultBucket(range: RangeMode): Bucket {
  const w = windowFor(range)
  const days = (w.toMs - w.fromMs) / 86_400_000
  if (days <= 2) return 'hour'
  if (days <= 92) return 'day'
  if (days <= 730) return 'week'
  return 'month'
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10)

export function renderFilters(
  state: FilterState,
  onChange: (next: FilterState) => void,
): HTMLElement {
  const presetRow = h(
    'div',
    { class: 'range-presets' },
    ...PRESETS.map((p) => {
      const active = state.range.kind === 'preset' && state.range.days === p.days
      const btn = h('button', { class: 'btn btn-sm', type: 'button', text: p.label })
      btn.setAttribute('aria-pressed', String(active))
      btn.addEventListener('click', () => {
        const range: RangeMode = { kind: 'preset', days: p.days }
        onChange({ ...state, range, bucket: defaultBucket(range) })
      })
      return btn
    }),
  )

  const now = new Date()
  const fallbackFrom = new Date(now.getTime() - 6 * 86_400_000)
  const from = h('input', {
    type: 'date',
    id: 'from-date',
    value: state.range.kind === 'custom' ? state.range.from : isoDay(fallbackFrom),
    max: isoDay(now),
  })
  const to = h('input', {
    type: 'date',
    id: 'to-date',
    value: state.range.kind === 'custom' ? state.range.to : isoDay(now),
    max: isoDay(now),
  })
  const applyCustom = () => {
    if (!from.value || !to.value) return
    // A backwards range returns nothing at all upstream; swap instead.
    const [a, b] = from.value <= to.value ? [from.value, to.value] : [to.value, from.value]
    const range: RangeMode = { kind: 'custom', from: a, to: b }
    onChange({ ...state, range, bucket: defaultBucket(range) })
  }
  from.addEventListener('change', applyCustom)
  to.addEventListener('change', applyCustom)

  const bucketSel = h('select', { id: 'bucket' }) as HTMLSelectElement
  for (const b of BUCKETS) {
    const opt = h('option', { value: b, text: b.charAt(0).toUpperCase() + b.slice(1) })
    if (b === state.bucket) opt.selected = true
    bucketSel.append(opt)
  }
  bucketSel.addEventListener('change', () => onChange({ ...state, bucket: bucketSel.value as Bucket }))

  const serverCb = h('input', { type: 'checkbox', id: 'server-period', checked: state.serverPeriod })
  serverCb.addEventListener('change', () => onChange({ ...state, serverPeriod: serverCb.checked }))

  const w = windowFor(state.range)
  return h(
    'div',
    { class: 'card filters' },
    h('div', { class: 'field' }, h('label', { text: 'Period' }), presetRow),
    h('div', { class: 'field' }, h('label', { htmlFor: 'from-date', text: 'From' }), from),
    h('div', { class: 'field' }, h('label', { htmlFor: 'to-date', text: 'To' }), to),
    h('div', { class: 'field' }, h('label', { htmlFor: 'bucket', text: 'Bucket' }), bucketSel),
    h(
      'div',
      { class: 'field' },
      h('label', { htmlFor: 'server-period', text: 'Aggregation' }),
      h(
        'label',
        { class: 'check', htmlFor: 'server-period', title: 'Send `period` to the cloud where the attribute offers it. Off = fetch raw rows and sum here.' },
        serverCb,
        h('span', { text: 'Server-side buckets' }),
      ),
    ),
    h(
      'div',
      { class: 'field', style: 'flex:1;min-width:200px' },
      h('label', { text: 'Window sent to the cloud' }),
      h('div', { class: 'mono muted', text: `${w.from} → ${w.to}` }),
    ),
  )
}
